import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { titleForPath } from './navConfig';
import { LogOut, User, Menu, Key, X, Loader2, Eye, EyeOff, RefreshCw, ExternalLink, Sun, Moon, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { errorMessage } from '../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  wh_manager: 'WH Manager',
  wh_executive: 'WH Executive'
};

const ChangePasswordModal = ({ isOpen, onClose, token }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(errorMessage(error, 'Failed to change password'));
      }

      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const fields = [
    { label: 'Current Password', value: currentPassword, set: setCurrentPassword, show: showCurrent, toggle: setShowCurrent, testid: 'current-password-input' },
    { label: 'New Password', value: newPassword, set: setNewPassword, show: showNew, toggle: setShowNew, testid: 'new-password-input', hint: 'Minimum 6 characters' },
    { label: 'Confirm New Password', value: confirmPassword, set: setConfirmPassword, show: showConfirm, toggle: setShowConfirm, testid: 'confirm-password-input' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="panel-head justify-between">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-slate-400" />
            <h3 className="panel-title">Change Password</h3>
          </div>
          <button onClick={onClose} className="btn-icon" data-testid="close-change-password">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {fields.map((f) => (
            <div key={f.testid}>
              <label className="label">{f.label}</label>
              <div className="relative">
                <input
                  type={f.show ? 'text' : 'password'}
                  value={f.value}
                  onChange={(e) => f.set(e.target.value)}
                  className={`input pr-10 ${f.testid === 'confirm-password-input' && confirmPassword && newPassword !== confirmPassword ? 'border-red-300' : ''}`}
                  required
                  data-testid={f.testid}
                />
                <button
                  type="button"
                  onClick={() => f.toggle(!f.show)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Toggle visibility"
                >
                  {f.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {f.hint && <p className="field-hint">{f.hint}</p>}
            </div>
          ))}
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-red-600">Passwords do not match</p>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button
              type="submit"
              disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
              className="btn-primary flex-1"
              data-testid="change-password-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const clearCaches = () => {
  localStorage.clear();
  sessionStorage.clear();
  if ('caches' in window) {
    caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
  }
  toast.success('Cache cleared. Reloading…');
  setTimeout(() => { window.location.href = window.location.origin; }, 500);
};

const Navbar = ({ onMenuClick }) => {
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isSsoUser = !!user?.auth_source;
  const isOpsLinked = !!(user?.emp_id || user?.auth_source);
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/login');
  };

  const openOperations = async () => {
    setShowUserMenu(false);
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/sso/operations-url`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_to: '/' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(errorMessage(data, 'Could not open Operations'));
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <header
        className="sticky top-0 z-20 h-14 border-b border-slate-200 bg-surface/95 backdrop-blur-sm"
        data-testid="top-bar"
      >
        <div className="h-full flex items-center gap-3 px-3 lg:px-5">
          <button
            onClick={onMenuClick}
            className="lg:hidden btn-icon"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-900 truncate" data-testid="page-title">
              {titleForPath(location.pathname)}
            </h1>
            <p className="eyebrow leading-tight hidden sm:block">
              {user?.name} · {roleLabels[user?.role]}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="btn-icon"
              data-testid="theme-toggle-btn"
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            >
              {theme === 'dark' ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </button>

            <div className="relative">
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                className="btn-ghost h-9 pl-2 pr-1.5 gap-2"
                data-testid="user-menu-btn"
              >
                <span className="w-6 h-6 rounded-md bg-brand-soft text-brand-soft-fg flex items-center justify-center flex-shrink-0">
                  <User className="w-3.5 h-3.5" />
                </span>
                <span className="hidden md:block text-xs font-semibold text-slate-800 max-w-[120px] truncate">
                  {user?.username}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>

              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 top-full mt-1.5 w-56 card z-20 py-1 shadow-pop animate-slide-up">
                    <div className="px-3 py-2 border-b border-slate-200">
                      <p className="text-xs font-semibold text-slate-900 truncate">{user?.name}</p>
                      <p className="text-2xs text-slate-500 truncate">{user?.username} · {roleLabels[user?.role]}</p>
                    </div>
                    {isOpsLinked && (
                      <button
                        onClick={openOperations}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 transition-colors"
                        data-testid="open-operations-btn"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Back to Operations
                      </button>
                    )}
                    {!isSsoUser && (
                      <button
                        onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 transition-colors"
                        data-testid="change-password-btn"
                      >
                        <Key className="w-3.5 h-3.5" />
                        Change Password
                      </button>
                    )}
                    <button
                      onClick={() => { setShowUserMenu(false); clearCaches(); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 transition-colors"
                      data-testid="clear-cache-btn"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Clear Cache & Reload
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); handleLogout(); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors border-t border-slate-200 mt-1"
                      data-testid="logout-menu-btn"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={handleLogout}
              className="btn-icon hover:text-red-600"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        token={token}
      />
    </>
  );
};

export default Navbar;
